import { Avatar, Group, Text } from '@mantine/core'
import React, {
  forwardRef,
  memo,
  useLayoutEffect,
  useRef,
  useState
} from 'react'

import setaDropdownVerde from '../../../icones/icone-seta-dropdown-verde.png'
import limparVerde from '../../../icones/iconesVersaoVermelha/icone-fechar-vermelho.svg'
import Loader from '../Loader'
import Tooltip from '../Tooltip'

import * as S from './styles'
import { GlobalStyles } from './styles'

const LIMITE_ITENS_DROPDOWN = 200

/**
 * @typedef {Object} Option
 * @property {string} label
 * @property {string} value
 * @property {string} [image]
 * @property {string} [description]
 * @property {React.ReactNode} [leftSection] - elemento renderizado à esquerda do label dentro do dropdown
 */

/**
 * @typedef {Object} SelectProps
 * @property {string | null | undefined} [value]
 * @property {(value: string | null) => void} [onChange]
 * @property {string} [placeholder]
 * @property {React.ReactNode} [label]
 * @property {string | number} [width]
 * @property {Option[]} [options]
 * @property {boolean} [disabled]
 * @property {boolean} [withAsterisk]
 * @property {number} [fontSize]
 * @property {boolean} [searchable]
 * @property {boolean} [clearable]
 * @property {string} [error]
 * @property {boolean} [isLoading]
 * @property {(value: string) => void} [onSearchChange] - callback ao digitar na busca (ex.: busca remota)
 */

/**
 * @param {SelectProps} props
 */
export default function Select({
  value,
  onChange,
  placeholder,
  label,
  width,
  options,
  disabled = false,
  withAsterisk = false,
  fontSize = 20,
  searchable = false,
  clearable = false,
  error,
  isLoading = false,
  withCard = false,
  onSearchChange
}) {
  const [searchValue, setSearchChange] = useState('')
  const [isOpen, setIsOpen] = useState(false)

  const refSelect = useRef()
  const justClosedRef = useRef(false)

  const widthText = refSelect?.current?.getBoundingClientRect()?.width

  const CustomSelectItem = forwardRef((props, ref) => (
    <SelectItem ref={ref} width={widthText} {...props} />
  ))

  CustomSelectItem.displayName = 'CustomSelectItem'

  const handleClear = (e) => {
    e.stopPropagation()
    if (onChange) {
      onChange(null)
    }
  }

  const handleOpenDropdown = (e) => {
    e.stopPropagation()

    // Se acabou de fechar, ignora esse clique para não reabrir
    if (justClosedRef.current) {
      justClosedRef.current = false
      return
    }

    const wrapper = refSelect.current?.closest('.mantine-Select-wrapper')
    const input = wrapper?.querySelector('input')

    if (input) {
      input.focus()
      input.dispatchEvent(
        new MouseEvent('mousedown', { bubbles: true, cancelable: true })
      )
    }
  }

  const showClearButton = clearable && value != null && !disabled

  const handleSearchChange = (val) => {
    setSearchChange(val)
    onSearchChange?.(val)
  }

  return (
    <>
      <GlobalStyles width={width} />
      <S.Select
        ref={refSelect}
        $withCard={withCard}
        label={label}
        fontSize={`${fontSize}px`}
        width={width && width}
        error={error}
        placeholder={placeholder}
        itemComponent={CustomSelectItem}
        withAsterisk={withAsterisk}
        searchable={searchable}
        clearable={false}
        withinPortal
        zIndex={9999}
        radius="xl"
        value={value}
        disabled={disabled}
        onSearchChange={handleSearchChange}
        searchValue={searchValue}
        nothingFound={
          isLoading && (!options || options.length === 0) ? (
            <S.LoadingWrapper>
              <Loader />
              <span>Carregando...</span>
            </S.LoadingWrapper>
          ) : (
            'Nenhuma opção encontrada'
          )
        }
        limit={LIMITE_ITENS_DROPDOWN}
        data={options || []}
        allowDeselect
        onChange={onChange}
        onDropdownOpen={() => {
          setIsOpen(true)
          justClosedRef.current = false
        }}
        onDropdownClose={() => {
          setIsOpen(false)
          justClosedRef.current = true
        }}
        rightSection={
          <S.RightSection onClick={handleOpenDropdown}>
            {showClearButton ? (
              <S.Img
                isOpen={isOpen}
                isClear={showClearButton}
                alt="limpar"
                src={limparVerde}
                onClick={(e) => {
                  e.stopPropagation()
                  handleClear(e)
                }}
              />
            ) : (
              <S.Img
                isOpen={isOpen}
                isClear={showClearButton}
                alt="seta-dropdown"
                src={setaDropdownVerde}
              />
            )}
          </S.RightSection>
        }
      />
    </>
  )
}

/**
 * @param {Object} props
 * @param {string} [props.image]
 * @param {React.ReactNode} [props.leftSection]
 * @param {string} props.label
 * @param {string} [props.description]
 * @param {number} [props.width]
 * @param {React.Ref<HTMLDivElement>} ref
 */
const GAP_GROUP = 5

const SelectItem = memo(
  forwardRef(
    ({ image, leftSection, label, description, width, ...others }, ref) => {
      const textRef = useRef(null)
      const leftRef = useRef(null)
      const [larguraLeft, setLarguraLeft] = useState(0)

      useLayoutEffect(() => {
        if (!leftRef.current) {
          setLarguraLeft(0)
          return undefined
        }
        const observer = new ResizeObserver(() => {
          const w = leftRef.current?.getBoundingClientRect()?.width || 0
          setLarguraLeft(w)
        })
        observer.observe(leftRef.current)
        setLarguraLeft(leftRef.current.getBoundingClientRect().width || 0)
        return () => observer.disconnect()
      }, [leftSection])

      const larguraTexto =
        width != null
          ? Math.max(width - larguraLeft - (larguraLeft ? GAP_GROUP : 0), 0)
          : undefined

      const showTooltip =
        larguraTexto != null &&
        textRef?.current?.scrollWidth > larguraTexto

      const domProps = Object.entries(others).reduce((acc, [key, value]) => {
        const isAriaOrData = key.startsWith('aria-') || key.startsWith('data-')
        const isDomEvent = key.startsWith('on')
        const isSafeDomProp = [
          'className',
          'style',
          'id',
          'role',
          'tabIndex',
          'title'
        ].includes(key)

        if (isAriaOrData || isDomEvent || isSafeDomProp) {
          acc[key] = value
        }

        return acc
      }, {})

      const conteudo = (
        <Group noWrap>
          {leftSection && <div ref={leftRef}>{leftSection}</div>}
          {image && <Avatar src={image} />}
          <div style={{ width: larguraTexto }}>
            <S.TextItem addOverflow={showTooltip} ref={textRef}>
              {label}
            </S.TextItem>
            {description && (
              <Text size="xs" opacity={0.65}>
                {description}
              </Text>
            )}
          </div>
        </Group>
      )

      return (
        <Tooltip
          disabled={!showTooltip}
          width={larguraTexto}
          withArrow
          label={label}
          position="bottom"
        >
          <div ref={ref} {...domProps}>
            {conteudo}
          </div>
        </Tooltip>
      )
    }
  )
)
SelectItem.displayName = 'SelectItem'
